/* eslint-disable complexity */
import {dateTimeParse} from '@gravity-ui/date-utils';
import {QuestionType} from 'common/constants/survey';
import {ChoiceQuestion, MatrixQuestion, Question, QuestionValue} from 'common/models/survey';
import {isEmpty} from 'lodash';

import isDateRange from './isDateRange';
import isMatrixValue from './isMatrixValue';
import isValidStringDate from './isValidStringDate';

const fixNumberValue = (value: QuestionValue) => {
    if (typeof value !== 'number') {
        const number = Number(value);
        return isNaN(number) ? null : number;
    }
    return value;
};

const getValidQuestionValue = (question: Question, value: QuestionValue) => {
    switch (question.type) {
        case QuestionType.Integer:
        case QuestionType.Payment: {
            return fixNumberValue(value);
        }
        case QuestionType.Enum:
        case QuestionType.Dropdown:
        case QuestionType.Radio:
        case QuestionType.Stars:
        case QuestionType.OneRow:
        case QuestionType.Checkbox: {
            if (!Array.isArray(value)) {
                return null;
            }
            const stringValues = (value as string[]).filter((option) => typeof option === 'string');
            const validValues = (question as ChoiceQuestion).items.reduce<string[]>((acc, item) => {
                if (stringValues.includes(item.id)) {
                    acc.push(item.id);
                }
                return acc;
            }, []);
            return validValues?.length > 0 ? validValues : null;
        }
        case QuestionType.Date: {
            if (isValidStringDate(value)) {
                return value;
            }
            return null;
        }
        case QuestionType.DateRange: {
            if (isValidStringDate(value)) {
                return value;
            }
            if (isDateRange(value)) {
                if (isValidStringDate(value.begin) && isValidStringDate(value.end)) {
                    const begin = dateTimeParse(value.begin);
                    const end = dateTimeParse(value.end);
                    return begin?.isBefore(end) ? value : {begin: value.end, end: value.begin};
                }
                if (
                    (isValidStringDate(value.begin) && isEmpty(value.end)) ||
                    (isValidStringDate(value.end) && isEmpty(value.begin))
                ) {
                    return value;
                }
                return null;
            }
            return null;
        }
        case QuestionType.Boolean: {
            return Boolean(value);
        }
        case QuestionType.Matrix: {
            if (!isMatrixValue(value) || !(question as MatrixQuestion).rows) {
                return null;
            }
            const rows = (question as MatrixQuestion).rows.map(({id}) => id);
            const valueMap = value.reduce<Record<string, string>>((acc, {row, column}) => {
                acc[row] = column;
                return acc;
            }, {});
            return rows.map((id) => {
                return {
                    row: id,
                    column: valueMap[id] ?? '',
                };
            });
        }
        default:
            return value;
    }
};

export default getValidQuestionValue;
